import React, { useContext } from "react";
import { AuthContext, FireactContext, SetPageTitle } from "../lib/core";
import { useNavigate } from "react-router-dom";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  where,
  updateDoc,
  doc,
  addDoc,
  serverTimestamp
} from "firebase/firestore";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardHeader,
  CardMedia,
  CardContent,
  Container,
  Paper,
  Typography,
  TextField
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { AppContext } from "./common/AppContext";

// Helper function to group notifications by date.
// It returns an array of groups in descending order of date.
const groupNotificationsByDate = (notifications) => {
  // Group notifications by a formatted date string.
  const groups = notifications.reduce((acc, notification) => {
    let dateStr = "Unknown Date";
    if (notification.createdAt) {
      // Convert Firestore timestamp to Date.
      const dateObj = typeof notification.createdAt.toDate === "function"
        ? notification.createdAt.toDate()
        : new Date(notification.createdAt);
      // Format date - e.g., "May 21, 2025"
      const options = { month: 'long', day: 'numeric', year: 'numeric' };
      dateStr = dateObj.toLocaleDateString(undefined, options);
    }
    if (!acc[dateStr]) {
      acc[dateStr] = [];
    }
    acc[dateStr].push(notification);
    return acc;
  }, {});

  // Convert the groups object to an array and sort by date descending.
  // Note: We assume that the formatted date string is parseable by Date.
  const groupedArray = Object.keys(groups)
    .sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateB - dateA;
    })
    .map(dateStr => ({
      date: dateStr,
      notifications: groups[dateStr]
    }));
  return groupedArray;
};

const NotificationRow = ({ notification, onMarkAsRead }) => {
  // Helper: Format the full date and time.
  function formatNotificationDate(timestamp) {
    const date = timestamp.toDate();
    const dateOptions = { month: 'long', day: 'numeric', year: 'numeric' };
    const timeOptions = { hour: 'numeric', minute: 'numeric' };
    const formattedDate = date.toLocaleDateString(undefined, dateOptions);
    const formattedTime = date.toLocaleTimeString(undefined, timeOptions);
    //return `${formattedDate} at ${formattedTime}`;
    return formattedTime;
  }

  const formattedDate = notification.createdAt
    ? formatNotificationDate(notification.createdAt)
    : 'Pending...';

  const handleClick = () => {
    if (!notification.read && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  return (
    <Card
      variant="outlined"
      onClick={handleClick}
      sx={{
        mb: 2,
        cursor: 'pointer',
        backgroundColor: notification.read ? 'white' : '#e1f5fe'
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* First Line: Icon (if unread), Title, and Date */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            {/* Left portion with Icon and Title */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Bell icon is shown only if unread */}
              {!notification.read && (
                <NotificationsIcon color="primary" sx={{ mr: 1 }} />
              )}
              <Typography variant="subtitle1" fontWeight="bold" noWrap>
                {notification.title}
              </Typography>
            </Box>
            {/* Right portion: Date */}
            <Typography variant="caption" color="text.secondary" noWrap>
              {formattedDate}
            </Typography>
          </Box>
          {/* Second Line: Notification Body */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} noWrap>
            {notification.body}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const NotificationsBell = () => {
  const { authInstance, firestoreInstance } = useContext(AuthContext);
  const navigate = useNavigate();
  const { authUser } = useContext(AuthContext);
  const { notifications } = useContext(AppContext);

  // Function to mark a notification as read.
  const markAsRead = async (notifId) => {
    const notifRef = doc(firestoreInstance, "notifications", notifId);
    await updateDoc(notifRef, { read: true });
  };

  // Group notifications by date using the helper function.
  const groupedNotifications = groupNotificationsByDate(notifications);

  return (
    <>
    <Container maxWidth={false} disableGutters>
        <SetPageTitle title="Notifications" />
        <Grid container spacing={0}>
        <Grid item xs={12} sx={{ p: 2, minHeight: "100vh" }}>
            {groupedNotifications.length === 0 ? (
            <Typography variant="h6" align="center" sx={{ mt: 2 }}>
                No notifications
            </Typography>
            ) : (
            groupedNotifications.map((group) => (
                <div key={group.date}>
                <Typography variant="h6" sx={{ pl: 2, pt: 2 }}>
                    {group.date}
                </Typography>
                {group.notifications.map((notification) => (
                    <NotificationRow
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={markAsRead}
                    />
                ))}
                </div>
            ))
            )}
        </Grid>
        </Grid>
    </Container>
    </>
  );
};

export default NotificationsBell;
